import { useEffect, useState, Dispatch, SetStateAction } from "react";
import {
  Button,
  Box,
  Dialog,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Popover,
  Select,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import {
  CalendarTodayOutlined,
  Close,
  BorderColor,
  Delete,
} from "@mui/icons-material";
import moment from "moment";
import {
  ColumnDirective,
  ColumnsDirective,
  GridComponent,
  Page,
  Sort,
  Filter,
  Toolbar,
  Inject,
  ContextMenu,
  RowDragEventArgs,
  Resize,
  DetailRow,
  Group,
} from "@syncfusion/ej2-react-grids";

import { useAppDispatch, useAppSelector } from "../../../../redux/store";
import {
  DeleteTaskById,
  GetMyTask,
  GetProjectById,
  PatchTaskById,
  ReorderTask,
  sortTask,
  GetAllTaskType,
} from "../../../../redux/features/project/taskManager-slice";

import AssignTaskModal from "./AssignTaskModal";
import DeleteConfirmation from "../../../../layouts/delete-confirmation/DeleteConfirmation";
import AddTaskModal from "./AddTaskModal";

import {
  // FilterData,
  Priority,
  Status,
} from "../../../../constants/taskmanagement";
import { ITask } from "../../../../types/taskManager";

import "./BoardTask.scss";
import { GetAllClientData } from "../../../../redux/features/client-main/client-list-slice";
import { GetAllProviders } from "../../../../redux/features/providers-main/provider-list-slice";
import {
  filterTaskData,
  IFilterData,
  ISortData,
  sortTaskData,
  getUniqueTaskNames,
  getDefaultFilterData,
} from "src/utils/TaskUtils";

export interface IUsers {
  id: string;
  name: string;
  profilePictureUrl: string;
}

interface TaskHeaderProps {
  dynamicFilterData: { label: string; data: string[] }[];
  filterData: IFilterData;
  setFilterData: Dispatch<SetStateAction<IFilterData>>;
  isSort: ISortData;
  setIsSort: Dispatch<SetStateAction<ISortData>>;
}

const TaskHeader: React.FC<TaskHeaderProps> = ({
  dynamicFilterData,
  filterData,
  setFilterData,
  isSort,
  setIsSort,
}) => {
  return (
    <div className="taskHeader">
      <Typography>Filter:</Typography>
      <div className="taskFormController">
        {dynamicFilterData.map(({ data, label }, index) => (
          <div key={index}>
            <FormControl key={index} className="task-outer">
              <InputLabel id="demo-simple-select-label">{label}</InputLabel>
              <Select
                className="form-control"
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={filterData[label]}
                style={{ height: "38px", width: "200px" }}
                onChange={(e) =>
                  setFilterData({ ...filterData, [label]: e.target.value })
                }
              >
                {data.map((item, index) => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {label === "TaskName" && (
              <div
                onClick={() => setIsSort({ ...isSort, task: !isSort.task })}
                style={{ color: "#939393", marginLeft: "-12px" }}
              >
                {/* {isSort.date ? <North /> : <South />} */}
              </div>
            )}
            {label === "DateRange" && (
              <div
                onClick={() => setIsSort({ ...isSort, date: !isSort.date })}
                style={{ color: "#939393", marginLeft: "-12px" }}
              >
                {/* {isSort.date ? <North /> : <South />} */}
              </div>
            )}
            {label === "Priority" && (
              <div
                onClick={() =>
                  setIsSort({ ...isSort, priority: !isSort.priority })
                }
                style={{ color: "#939393", marginLeft: "-12px" }}
              >
                {/* {isSort.priority ? <North /> : <South />} */}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const BoardTask: React.FC<{ projectId: string }> = ({ projectId }) => {
  const dispatch = useAppDispatch();
  const [statusAnchor, setStatusAnchor] = useState<
    HTMLButtonElement | HTMLDivElement | null
  >(null);
  let openStatusAnchor = Boolean(statusAnchor);
  const [priorityAnchor, setPriorityAnchor] = useState<
    HTMLButtonElement | HTMLDivElement | null
  >(null);
  let openPriorityAnchor = Boolean(priorityAnchor);
  const id =
    openStatusAnchor || openPriorityAnchor ? "simple-popover" : undefined;
  const [assignTaskModal, setAssignTaskModal] = useState<boolean>(false);
  const [filterData, setFilterData] = useState<IFilterData>({
    TaskName: "",
    DateRange: "",
    Priority: "",
    Status: "",
  });
  const [assignedTo, setAssignedTo] = useState<IUsers | null>();
  const tasks = useAppSelector((state) => state.taskManagerSlice.tasks);
  const [currentTask, setCurrentTask] = useState<ITask>();
  const [isDelete, setIsDelete] = useState<boolean>(false);
  const [addTaskModal, setAddTaskModal] = useState<boolean>(false);
  const [isSortData, setIsSortData] = useState<ISortData>({
    task: false,
    date: false,
    priority: false,
  });

  const Allclients = useAppSelector((state) => state.clientListSlice.listData);
  const Allproviders = useAppSelector(
    (state) => state.providerListSlice.listData
  );

  // After you get the sorted and filtered tasks:
  const sortedTasks = sortTaskData(
    filterTaskData(tasks, filterData),
    isSortData
  );

  // Extract unique TaskNames
  const uniqueTaskNames = getUniqueTaskNames(sortedTasks);
  const dynamicFilterData = getDefaultFilterData(uniqueTaskNames);

  // Store the final processed tasks for rendering
  const processedTasks = sortedTasks;

  //deletes tasks
  const deleteTask = () => {
    if (currentTask?.id) {
      dispatch(
        DeleteTaskById({
          id: currentTask.id,
        })
      );
    }
  };

  //updates status of task
  const handleUpdate = (type: string, value: string) => {
    if (currentTask) {
      dispatch(
        PatchTaskById({
          type: type, //"status",
          value: value,
          id: currentTask.id,
        })
      );
    }
    // Clean up fake elements
    if (statusAnchor && statusAnchor.parentNode) {
      statusAnchor.parentNode.removeChild(statusAnchor);
    }
    if (priorityAnchor && priorityAnchor.parentNode) {
      priorityAnchor.parentNode.removeChild(priorityAnchor);
    }
    setStatusAnchor(null);
    setPriorityAnchor(null);
  };

  const handleAssignTask = () => {
    if (currentTask) {
      if (assignedTo && currentTask.id) {
        dispatch(
          PatchTaskById({
            type: "assign",
            value: assignedTo?.id,
            id: currentTask.id,
          })
        );
      }
    }

    setAssignTaskModal(false);
  };

  // Cleanup function for popover close
  const handleStatusPopoverClose = () => {
    if (statusAnchor && statusAnchor.parentNode) {
      statusAnchor.parentNode.removeChild(statusAnchor);
    }
    setStatusAnchor(null);
  };

  const handlePriorityPopoverClose = () => {
    if (priorityAnchor && priorityAnchor.parentNode) {
      priorityAnchor.parentNode.removeChild(priorityAnchor);
    }
    setPriorityAnchor(null);
  };

  const handleTaskFor = (entityType: string, entityId: string) => {
    let name = "";

    if (entityType === "Client") {
      Allclients.forEach((client) => {
        if (client.clientBasicDetails.id === entityId) {
          name =
            client.clientBasicDetails.childFirstName +
            " " +
            client.clientBasicDetails.childLastName;
        }
      });
    } else {
      Allproviders.forEach((provider) => {
        if (provider.employmentDetails.id === entityId) {
          name =
            provider.employmentDetails.firstName +
            " " +
            provider.employmentDetails.lastName;
        }
      });
    }

    return name;
  };

  // Transform tasks for Syncfusion Grid
  const gridData = processedTasks.map((task) => ({
    id: task.id,
    assignedTo: task?.assignedTo
      ? task?.assignedTo.firstName + " " + task?.assignedTo.lastName
      : "",
    name: task?.name,
    type: task?.type,
    taskFor: handleTaskFor(task.entityType, task.entityId),
    dueDate: task.dueDate ? moment(task.dueDate).format("LL") : "",
    priority: task?.priority,
    status: task?.status,
    complete: `${
      task?.checklist?.filter((item) => item.checked === true).length
    } / ${task?.checklist?.length}`,
    createdOn: moment.utc(task?.createdAt).format("MM/DD/YYYY"),
    originalTask: task, // Keep reference to original task
  }));

  // Action template for edit/delete buttons
  const actionTemplate = (props: any) => {
    const task = props.originalTask;
    return (
      <div style={{ display: "flex", gap: "15px" }}>
        <BorderColor
          sx={{ cursor: "pointer" }}
          style={{ color: "#096DD9" }}
          onClick={() => {
            setAddTaskModal(true);
            setCurrentTask(task);
          }}
        />
        <Delete
          sx={{ cursor: "pointer" }}
          style={{ color: "#096DD9" }}
          onClick={() => {
            setIsDelete(true);
            setCurrentTask(task);
          }}
        />
      </div>
    );
  };

  // Priority template with styling
  // const priorityTemplate = (props: any) => {
  //   return (
  //     <div className={`task_priority_${props.priority}`}>{props.priority}</div>
  //   );
  // };

  // Status template with styling
  const statusTemplate = (props: any) => {
    const task = props.originalTask;
    return (
      <div
        className={`task-status-${props.status.replace(" ", "")}`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          const rect = e.currentTarget.getBoundingClientRect();
          const fakeElement = document.createElement("div");
          fakeElement.setAttribute("data-fake-anchor", "true");
          fakeElement.style.position = "fixed";
          fakeElement.style.top = `${rect.bottom}px`;
          fakeElement.style.left = `${rect.left}px`;
          fakeElement.style.width = `${rect.width}px`;
          fakeElement.style.height = "1px";
          document.body.appendChild(fakeElement);
          setStatusAnchor(fakeElement as any);
          setCurrentTask(task);
        }}
        style={{ cursor: "pointer" }}
      >
        {props.status}
      </div>
    );
  };

  // Assigned to template with click handler
  const assignedToTemplate = (props: any) => {
    const task = props.originalTask;
    return (
      <div
        onClick={() => {
          if (task.assignedTo !== null) {
            setAssignedTo({
              id: task.assignedTo.id,
              name: task.assignedTo.firstName + " " + task.assignedTo.lastName,
              profilePictureUrl: task.assignedTo.profilePictureUrl,
            });
          } else {
            setAssignedTo(null);
          }
          setCurrentTask(task);
          setAssignTaskModal(true);
        }}
        style={{ cursor: "pointer" }}
      >
        <Typography className="taskDetails_main">{props.assignedTo}</Typography>
      </div>
    );
  };

  // Due date template with date picker
  const dueDateTemplate = (props: any) => {
    const task = props.originalTask;
    return (
      <div className="calender-outer">
        <input
          min={
            task.dueDate
              ? moment.utc(task.dueDate).format("MM/DD/YYYY")
              : new Date().toLocaleString()
          }
          style={{ position: "absolute", opacity: "0" }}
          type="date"
          onChange={(e) => {
            handleUpdate(
              "dueDate",
              moment.utc(e.target.value).format("MM/DD/YYYY")
            );
            setCurrentTask(task);
          }}
        />
        <CalendarTodayOutlined />
        {props.dueDate}
      </div>
    );
  };

  // Priority click handler
  const priorityClickHandler = (props: any) => {
    const task = props.originalTask;
    return (
      <div
        className={`task_priority_${props.priority}`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          const rect = e.currentTarget.getBoundingClientRect();
          const fakeElement = document.createElement("div");
          fakeElement.setAttribute("data-fake-anchor", "true");
          fakeElement.style.position = "fixed";
          fakeElement.style.top = `${rect.bottom}px`;
          fakeElement.style.left = `${rect.left}px`;
          fakeElement.style.width = `${rect.width}px`;
          fakeElement.style.height = "1px";
          document.body.appendChild(fakeElement);
          setPriorityAnchor(fakeElement as any);
          setCurrentTask(task);
        }}
        style={{ cursor: "pointer" }}
      >
        {props.priority}
      </div>
    );
  };
  const onRowDrop = (args: RowDragEventArgs) => {
    // Prevent default behavior
    args.cancel = true;

    // Handle the drag and drop logic
    const draggedRowIndex = args.fromIndex;
    const targetRowIndex = args.dropIndex;

    if (projectId === "My Tasks") return;
    if (targetRowIndex === undefined || draggedRowIndex === undefined) return;
    if (draggedRowIndex === targetRowIndex) return;

    // Create new array with reordered items
    const dragItem = processedTasks[draggedRowIndex];
    const newTasks = [...processedTasks];
    newTasks.splice(draggedRowIndex, 1);
    newTasks.splice(targetRowIndex, 0, dragItem);

    // Update Redux store
    dispatch(sortTask(newTasks));

    // Create reorder object for API call
    const reorderObj: Record<string, number> = {};
    newTasks.forEach(({ id }, index) => (reorderObj[id] = index));
    dispatch(ReorderTask(reorderObj));
  };

  //#region useEffect region
  useEffect(() => {
    if (projectId === "My Tasks") {
      dispatch(GetMyTask());
    } else {
      dispatch(GetProjectById(projectId));
    }
    setFilterData({
      TaskName: "",
      DateRange: "",
      Priority: "",
      Status: "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    dispatch(GetAllTaskType());
    if (Allclients.length === 0) {
      dispatch(GetAllClientData("full"));
    }
    if (Allproviders.length === 0) {
      dispatch(GetAllProviders("full"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cleanup effect for fake elements
  useEffect(() => {
    return () => {
      // Clean up any remaining fake elements on unmount
      const fakeElements = document.querySelectorAll("[data-fake-anchor]");
      fakeElements.forEach((el) => {
        if (el.parentNode) {
          el.parentNode.removeChild(el);
        }
      });
    };
  }, []);

  return (
    <div>
      <TaskHeader
        dynamicFilterData={dynamicFilterData}
        filterData={filterData}
        setFilterData={setFilterData}
        isSort={isSortData}
        setIsSort={setIsSortData}
      />
      <Grid size={{ xs: 12 }} className="listingTable">
        <Paper className="tableResponsive">
          <GridComponent
            id="grid" //must have!!
            dataSource={gridData}
            allowFiltering={true}
            allowSorting={true}
            allowGrouping={true}
            allowPaging={true}
            height={600}
            filterSettings={{ type: "Menu" }}
            width="100%"
            rowDrop={onRowDrop}
            className="board-task-grid gridTable"
          >
            <ColumnsDirective>
              <ColumnDirective
                field="assignedTo"
                headerText="Assign To"
                width="150"
                template={assignedToTemplate}
              />
              <ColumnDirective
                field="name"
                headerText="Task Name"
                width="200"
                template={(props: any) => (
                  <Typography className="taskDetails_main_wrap">
                    {props.name}
                  </Typography>
                )}
              />
              <ColumnDirective
                field="type"
                headerText="Task Type"
                width="120"
              />
              <ColumnDirective
                field="taskFor"
                headerText="Task For"
                width="150"
              />
              <ColumnDirective
                field="dueDate"
                headerText="Due Date"
                width="150"
                template={dueDateTemplate}
              />
              <ColumnDirective
                field="priority"
                headerText="Priority"
                width="100"
                template={priorityClickHandler}
              />
              <ColumnDirective
                field="status"
                headerText="Status"
                width="120"
                template={statusTemplate}
              />
              <ColumnDirective
                field="complete"
                headerText="Complete"
                width="100"
              />
              <ColumnDirective
                field="createdOn"
                headerText="Created On"
                width="120"
              />
              <ColumnDirective
                field="action"
                headerText="Action"
                width="100"
                template={actionTemplate}
              />
            </ColumnsDirective>
            <Inject
              services={[
                Page,
                Group,
                ContextMenu,
                Toolbar,
                Resize,
                DetailRow,
                Sort,
                Filter,
              ]}
            />
          </GridComponent>
        </Paper>
        <Popover
          id={id + "status"}
          open={openStatusAnchor}
          onClose={handleStatusPopoverClose}
          anchorEl={statusAnchor as HTMLElement}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
        >
          <div style={{ width: "138px", padding: "10px" }}>
            {Object.values(Status).map((item, index) => (
              <MenuItem
                key={index}
                className={`task-status-${item.replace(" ", "")} mb-10`}
                value={item}
                onClick={() => handleUpdate("status", item)}
              >
                {item}
              </MenuItem>
            ))}
          </div>
        </Popover>
        <Popover
          id={id + "status"}
          open={openPriorityAnchor}
          onClose={handlePriorityPopoverClose}
          anchorEl={priorityAnchor as HTMLElement}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
        >
          <div style={{ width: "138px", padding: "10px" }}>
            {Object.values(Priority).map((item, index) => {
              return (
                <MenuItem
                  key={index}
                  className={`task-status-${item.replace(" ", "")} mb-10`}
                  value={item}
                  onClick={() => handleUpdate("priority", item)}
                >
                  {item}
                </MenuItem>
              );
            })}
          </div>
        </Popover>
      </Grid>
      {assignTaskModal && (
        <Dialog open={assignTaskModal} className="boardLargeModel">
          <div>
            <Grid>
              <Box className="largeModel__head">
                <Typography variant="h5" className="largeModel__title">
                  Assign To:
                </Typography>
                <Button
                  className="largeModel__closeBtn"
                  onClick={() => setAssignTaskModal(false)}
                >
                  <Close />
                </Button>
              </Box>
            </Grid>
          </div>
          <div className="create-new-board">
            <div className="create-new-board-content">
              <AssignTaskModal
                assignedTo={assignedTo}
                setAssignedTo={setAssignedTo}
                fromModal={false}
              />
            </div>
            <Box className="taskModal-footer">
              <Button onClick={() => handleAssignTask()} variant="contained">
                Assign
              </Button>
            </Box>
          </div>
        </Dialog>
      )}
      {isDelete && (
        <DeleteConfirmation
          name={"Task"}
          setModal={(value) => {
            if (value) {
              deleteTask();
            }
            setIsDelete(false);
          }}
        />
      )}
      {addTaskModal && (
        <Dialog open={addTaskModal} className="boardLargeModel">
          <AddTaskModal
            setAddTaskModal={setAddTaskModal}
            isEdit={true}
            currentTask={currentTask}
          />
        </Dialog>
      )}
    </div>
  );
};

export default BoardTask;
